import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import upzeraLogo from "../assets/upzera-logo.png";
import {
  Code,
  Users,
  BookOpen,
  GraduationCap,
  Mail,
  Phone,
  MapPin,
  Laptop,
  FileSpreadsheet,
  MessageCircle,
  Star,
  TrendingUp,
  Zap,
  Brain,
  Cpu,
  Database,
} from "lucide-react";

// Comprehensive Course Data
const courses = [
  {
    category: "Programming Languages",
    items: [
      {
        title: "Java",
        description:
          "Comprehensive Java programming course from basics to advanced concepts",
        icon: <Code className="w-12 h-12 text-blue-500" />,
      },
      {
        title: "JavaScript",
        description:
          "Full JavaScript programming with modern frameworks and techniques",
        icon: <Code className="w-12 h-12 text-yellow-500" />,
      },
      {
        title: "MERN Stack",
        description:
          "Complete MongoDB, Express, React, Node.js full-stack development",
        icon: <Laptop className="w-12 h-12 text-green-500" />,
      },
      {
        title: "MEAN Stack",
        description:
          "MongoDB, Express, Angular, Node.js comprehensive web development",
        icon: <Laptop className="w-12 h-12 text-indigo-500" />,
      },
      {
        title: "Database Technologies",
        description: "Oracle, MySQL, PostgreSQL database management and design",
        icon: <Database className="w-12 h-12 text-purple-500" />,
      },
      {
        title: "Web Technologies",
        description: "HTML, CSS, Bootstrap, front-end development essentials",
        icon: <Code className="w-12 h-12 text-red-500" />,
      },
      {
        title: "Python",
        description:
          "Python programming from fundamentals to advanced applications",
        icon: <Code className="w-12 h-12 text-green-600" />,
      },
      {
        title: "C & C++",
        description: "System programming and advanced software development",
        icon: <Code className="w-12 h-12 text-blue-600" />,
      },
      {
        title: "Embeded",
        description: "System programming and advanced Hardware development",
        icon: <Cpu className="w-12 h-12 text-orange-600" />,
      },
    ],
  },
  {
    category: "Soft Skills",
    items: [
      {
        title: "Excel Mastery",
        description:
          "Advanced spreadsheet skills for data analysis and management",
        icon: <FileSpreadsheet className="w-12 h-12 text-green-500" />,
      },
      {
        title: "PowerPoint Pro",
        description:
          "Create compelling presentations with professional design techniques",
        icon: <Laptop className="w-12 h-12 text-blue-500" />,
      },
      {
        title: "English Communication",
        description: "Improve verbal and written communication skills",
        icon: <MessageCircle className="w-12 h-12 text-purple-500" />,
      },
      {
        title: "Teamwork & Collaboration",
        description: "Develop effective team working and interpersonal skills",
        icon: <Users className="w-12 h-12 text-red-500" />,
      },
      {
        title: "Leadership Skills",
        description: "Develop strategic thinking and leadership capabilities",
        icon: <TrendingUp className="w-12 h-12 text-yellow-500" />,
      },
    ],
  },
];

// Array of tech words for the background
const techWords = [
  "JavaScript",
  "Python",
  "React",
  "AI",
  "Machine Learning",
  "Data Science",
  "Cloud",
  "DevOps",
  "Blockchain",
  "Cybersecurity",
  "IoT",
  "AR/VR",
  "Java",
  "HTML5",
  "CSS3",
  "NodeJS",
  "MongoDB",
  "Express",
  "API",
  "Git",
  "Agile",
  "Scrum",
  "UI/UX",
  "BigData",
  "SQL",
  "NoSQL",
  "Docker",
  "Kubernetes",
  "AWS",
  "Azure",
  "Firebase",
  "TypeScript",
  "Angular",
  "Vue",
  "GraphQL",
  "Redux",
  "TensorFlow",
  "PyTorch",
  "NLP",
  "Computer Vision",
  "Robotics",
];

// Logo component for Upzera
const UpzeraLogo = () => (
  // <svg
  //   width="40"
  //   height="40"
  //   viewBox="0 0 200 200"
  //   fill="none"
  //   xmlns="http://www.w3.org/2000/svg"
  // >
  //   <circle cx="100" cy="100" r="95" fill="#2563EB" />
  //   <path d="M50 70 V 150 H 70 V 70 H 50 Z" fill="white" />
  //   <path d="M80 70 H 150 V 90 H 80 V 70 Z" fill="white" />
  //   <path d="M80 100 H 150 V 120 H 80 V 100 Z" fill="white" />
  //   <path d="M80 130 H 150 V 150 H 80 V 130 Z" fill="white" />
  // </svg>
  <img
    src={upzeraLogo}
    alt="Upzera Logo"
    width={30}
    height={30}
    style={{ borderRadius: "60%", objectFit: "cover" }}
  />
);

// Floating Tech Word Component
const FloatingWord = ({ word, delay, duration, size }) => {
  return (
    <motion.div
      initial={{
        opacity: 0,
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
      }}
      animate={{
        opacity: [0, 0.7, 0.5, 0.7, 0],
        x: [null, Math.random() * window.innerWidth],
        y: [null, Math.random() * window.innerHeight],
      }}
      transition={{
        duration: duration,
        delay: delay,
        repeat: Infinity,
        repeatType: "loop",
      }}
      className={`absolute text-${size} font-light text-blue-300/20 pointer-events-none select-none`}
    >
      {word}
    </motion.div>
  );
};

// Circuit Line Component
const CircuitLine = ({ startX, startY, length, angle, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: [0, 0.5, 0.8, 0.5, 0], scale: 1 }}
      transition={{
        duration: 8,
        delay: delay,
        repeat: Infinity,
        repeatType: "loop",
      }}
      style={{
        position: "absolute",
        top: startY,
        left: startX,
        width: length,
        height: "2px",
        background:
          "linear-gradient(90deg, rgba(37,99,235,0) 0%, rgba(37,99,235,0.3) 50%, rgba(37,99,235,0) 100%)",
        transform: `rotate(${angle}deg)`,
        transformOrigin: "0 0",
      }}
    />
  );
};

// Neural Node Component
const NeuralNode = ({ x, y, size, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: [0, 0.7, 0.3, 0.7, 0], scale: [0, 1, 0.8, 1, 0] }}
      transition={{
        duration: 5,
        delay: delay,
        repeat: Infinity,
        repeatType: "loop",
      }}
      style={{
        position: "absolute",
        top: y,
        left: x,
        width: size,
        height: size,
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(56,189,248,0.3) 0%, rgba(56,189,248,0) 70%)",
      }}
    />
  );
};

// Floating particle component
const Particle = ({ delay }) => {
  const size = Math.random() * 4 + 2;
  return (
    <motion.div
      initial={{
        opacity: 0,
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        scale: 0,
      }}
      animate={{
        opacity: [0, 0.7, 0.3, 0.7, 0],
        y: [null, Math.random() * window.innerHeight],
        scale: [0, 1, 0.8, 1, 0],
      }}
      transition={{
        duration: Math.random() * 5 + 3,
        delay: delay,
        repeat: Infinity,
        repeatType: "loop",
      }}
      style={{
        position: "absolute",
        width: size,
        height: size,
        borderRadius: "50%",
        background: `rgba(${Math.floor(
          Math.random() * 100 + 155
        )}, ${Math.floor(Math.random() * 100 + 155)}, 255, 0.7)`,
      }}
    />
  );
};

const EducationPlatform = () => {
  const [activeTab, setActiveTab] = useState("Programming Languages");
  const [courseCount, setCourseCount] = useState(0);
  const [studentCount, setStudentCount] = useState(0);
  const contactRef = useRef(null);

  // Dynamic Counter Effect
  useEffect(() => {
    const courseTimer = setInterval(() => {
      setCourseCount((prev) => (prev < 10 ? prev + 1 : 10));
    }, 50);

    const studentTimer = setInterval(() => {
      setStudentCount((prev) => (prev < 1000 ? prev + 50 : 1000));
    }, 20);

    return () => {
      clearInterval(courseTimer);
      clearInterval(studentTimer);
    };
  }, []);

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Animation variants for the right-to-left motion
  const futureSkillsVariants = {
    initial: { x: 1000, opacity: 0 },
    animate: { x: 0, opacity: 1, transition: { duration: 0.8, delay: 0.8 } },
  };

  // Generate circuit lines
  const circuitLines = Array.from({ length: 15 }, (_, i) => ({
    startX: Math.random() * window.innerWidth,
    startY: Math.random() * window.innerHeight,
    length: Math.random() * 200 + 50,
    angle: Math.random() * 360,
    delay: Math.random() * 5,
  }));

  // Generate neural nodes
  const neuralNodes = Array.from({ length: 20 }, (_, i) => ({
    x: Math.random() * window.innerWidth,
    y: Math.random() * window.innerHeight,
    size: Math.random() * 40 + 10,
    delay: Math.random() * 5,
  }));

  // Generate floating particles
  const particles = Array.from({ length: 50 }, (_, i) => ({
    delay: Math.random() * 10,
  }));

  return (
    <div className="bg-gradient-to-br from-gray-900 to-blue-900 text-white relative overflow-hidden">
      {/* Dynamic Background Elements */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 opacity-50 animate-pulse"></div>

        {/* Grid Pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(rgba(37, 99, 235, 0.1) 1px, transparent 1px), radial-gradient(rgba(37, 99, 235, 0.1) 1px, transparent 1px)",
            backgroundSize: "30px 30px",
            backgroundPosition: "0 0, 15px 15px",
          }}
        ></div>

        {/* Animated circuit-like lines */}
        {circuitLines.map((line, index) => (
          <CircuitLine
            key={`line-${index}`}
            startX={line.startX}
            startY={line.startY}
            length={line.length}
            angle={line.angle}
            delay={line.delay}
          />
        ))}

        {/* Neural network nodes */}
        {neuralNodes.map((node, index) => (
          <NeuralNode
            key={`node-${index}`}
            x={node.x}
            y={node.y}
            size={node.size}
            delay={node.delay}
          />
        ))}

        {/* Floating tech words */}
        {techWords.slice(0, 20).map((word, index) => (
          <FloatingWord
            key={`word-${index}`}
            word={word}
            delay={index * 0.3}
            duration={Math.random() * 15 + 15}
            size={Math.random() > 0.7 ? "xl" : "lg"}
          />
        ))}

        {/* Floating particles */}
        {particles.map((particle, index) => (
          <Particle key={`particle-${index}`} delay={particle.delay} />
        ))}
      </div>

      {/* Company Logo and Name in Top Left */}
      <div className="fixed top-4 left-4 z-50 flex items-center space-x-2 bg-black/30 p-2 rounded-lg backdrop-blur-md">
        <UpzeraLogo />
        <span className="text-2xl font-bold text-white">UPZERA</span>
      </div>

      {/* Home/Hero Section */}
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="h-screen flex flex-col justify-center items-center relative overflow-hidden z-10"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-800/30 to-purple-800/30 backdrop-blur-sm"></div>

        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.1 }}
          transition={{ delay: 0.2, duration: 1.5 }}
          className="absolute w-96 h-96 rounded-full bg-blue-500/20 blur-3xl"
        />

        <motion.h1
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="text-6xl font-bold text-center z-10"
        >
          Empowering College Students
          <br />
          with
          <motion.span
            variants={futureSkillsVariants}
            initial="initial"
            animate="animate"
            className="ml-2 inline-block text-gradient bg-clip-text text-transparent bg-gradient-to-r from-amber-400 to-amber-400"
          >
            Future-Ready Skills
          </motion.span>
        </motion.h1>
      </motion.header>

      {/* Dynamic Counters */}
      <section className="bg-black/30 backdrop-blur-md py-12 flex justify-around relative z-10">
        <div className="text-center">
          <h3 className="text-4xl font-bold text-green-400">{courseCount}+</h3>
          <p>Courses Offered</p>
        </div>
        <div className="text-center">
          <h3 className="text-4xl font-bold text-blue-400">{studentCount}+</h3>
          <p>Students Trained</p>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-16 px-8 bg-gray-800/30 backdrop-blur-md relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-8">About Our Institute</h2>
          <p className="text-xl text-gray-300">
            We are dedicated to providing high-quality education in programming
            and soft skills. Our mission is to bridge the gap between academic
            learning and industry requirements, empowering students with
            practical, job-ready skills.
          </p>
          <div className="flex justify-center mt-8 space-x-4">
            <div className="bg-blue-900/50 p-6 rounded-xl backdrop-blur-md border border-blue-500/20">
              <Star className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
              <h3 className="text-2xl font-semibold">Our Mission</h3>
              <p className="text-gray-300">
                Transforming potential into professional excellence
              </p>
            </div>
            <div className="bg-blue-900/50 p-6 rounded-xl backdrop-blur-md border border-blue-500/20">
              <Zap className="w-16 h-16 mx-auto text-green-400 mb-4" />
              <h3 className="text-2xl font-semibold">Our Vision</h3>
              <p className="text-gray-300">
                Creating industry-ready tech professionals
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="py-16 px-8 relative z-10">
        <div className="flex justify-center mb-8">
          {["Programming Languages", "Soft Skills"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`mx-4 px-6 py-2 rounded-full transition-all ${
                activeTab === tab
                  ? "bg-blue-600 text-white"
                  : "bg-gray-800/80 backdrop-blur-md text-gray-400 hover:bg-gray-700/80"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-4 gap-8">
          {courses
            .find((cat) => cat.category === activeTab)
            .items.map((course, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05, rotate: 2 }}
                className="bg-gray-800/50 backdrop-blur-lg p-6 rounded-xl border border-white/10 shadow-2xl"
              >
                {course.icon}
                <h4 className="text-2xl mt-4 font-semibold">{course.title}</h4>
                <p className="text-gray-300 mt-2">{course.description}</p>
              </motion.div>
            ))}
        </div>
      </section>

      {/* Contact Us Section */}
      <section
        ref={contactRef}
        className="py-16 px-8 bg-gray-800/30 backdrop-blur-md relative z-10"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">Contact Us</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center bg-blue-900/30 p-6 rounded-xl backdrop-blur-md border border-blue-500/20">
              <Mail className="w-16 h-16 mx-auto mb-4 text-blue-400" />
              <h3 className="text-2xl font-semibold">Email</h3>
              <p className="text-gray-300">upzera.office@gmail.com</p>
            </div>
            <div className="text-center bg-blue-900/30 p-6 rounded-xl backdrop-blur-md border border-blue-500/20">
              <Phone className="w-16 h-16 mx-auto mb-4 text-green-400" />
              <h3 className="text-2xl font-semibold">Phone</h3>
              <p className="text-gray-300">+91 8248693010</p>
              <p className="text-gray-300">+91 9524331199</p>
            </div>
            <div className="text-center bg-blue-900/30 p-6 rounded-xl backdrop-blur-md border border-blue-500/20">
              <MapPin className="w-16 h-16 mx-auto mb-4 text-red-400" />
              <h3 className="text-2xl font-semibold">Address</h3>
              <p className="text-gray-300">Coimbatore,Tamil Nadu</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EducationPlatform;
